/**
 * Chord vocabulary, identification, and construction.
 *
 * Two directions are supported and they share one table:
 *   - Analysis  : a set of written pitches -> chord name(s) with confidence.
 *   - Synthesis : a quality + one known degree -> the whole chord (this is Chord FIB).
 */

import {
  LETTERS,
  MAJOR_SCALE_SEMITONES,
  diatonicStep,
  letterIndex,
  midi,
  naturalPitchClass,
  pitchClass,
  pitchName,
  transposeBy,
  type Letter,
  type Pitch,
} from './pitch';

export interface ChordTone {
  /** Diatonic distance above the root: 0 = root, 2 = a third, 4 = a fifth, 6 = a seventh. */
  diatonic: number;
  /** Semitones above the root. */
  semitones: number;
}

/**
 * How the Chord Legend groups qualities.
 *
 * A quality may belong to more than one: a sus4 is a triad *and* a suspended
 * chord, and hiding it from either filter would be wrong. The array is the
 * domain's own statement of that, so no screen has to decide it — and a test
 * asserts every quality lands in at least one, and that no filter is empty.
 */
export type ChordCategory =
  | 'triad'
  | 'suspended'
  | 'sixth-added'
  | 'seventh'
  | 'extended'
  | 'altered';

export const CHORD_CATEGORIES: { id: ChordCategory; label: string }[] = [
  { id: 'triad', label: 'Triads' },
  { id: 'seventh', label: 'Seventh' },
  { id: 'sixth-added', label: 'Sixth and added' },
  { id: 'extended', label: 'Extended' },
  { id: 'altered', label: 'Altered' },
  { id: 'suspended', label: 'Suspended' },
];

export interface ChordQuality {
  id: string;
  /** Short symbol appended to the root, e.g. "maj7" in "Cmaj7". */
  symbol: string;
  /** Full name for teaching text. */
  name: string;
  tones: ChordTone[];
  /** Families group the picker UI. */
  family: 'triad' | 'seventh' | 'sixth' | 'extended' | 'altered';
  /**
   * Which Legend filters this quality appears under. At least one, sometimes
   * two — see `ChordCategory`.
   */
  categories: ChordCategory[];
  /**
   * One short sentence a beginner can read, about the *formula*.
   *
   * Deliberately separate from `cause`, which explains why the chord sounds the
   * way it does. This says what the numbers mean. Both live here rather than in
   * a screen, so the Legend cannot drift from the rest of the app.
   */
  blurb: string;
  /**
   * What a learner might *type* for this chord, when the printed symbol is a
   * glyph they cannot reach on a keyboard.
   *
   * `ø7` is written that way and typed `m7b5`; `°7` is typed `dim7`. Without
   * this the search had to fall back on the internal `id`, which is not a name
   * — and using an identifier as though it were one is what made `m7b5` return
   * two different chords.
   *
   * Deliberately not a place for family words. `dim` is *not* an alias of the
   * diminished triad: it is the start of "diminished" and of "dim7", and a
   * learner typing it is browsing rather than naming. Aliases are for exact
   * names only; anything broader belongs to the substring search.
   */
  aliases?: string[];
  /**
   * Degrees a playable voicing usually leaves out.
   *
   * The full formula is the theory; this is the practice. They must never be
   * confused — showing a four-note voicing of a thirteenth as though it *were*
   * the chord teaches a formula that is missing three of its notes.
   */
  commonlyOmitted?: string[];
  /** Why this chord sounds the way it does — shown in the app, not just a label. */
  cause: string;
  /** Ranked lower when two qualities share the same pitch classes (e.g. dim7 inversions). */
  priority: number;
}

const t = (diatonic: number, semitones: number): ChordTone => ({ diatonic, semitones });

export const QUALITIES: ChordQuality[] = [
  {
    id: 'maj',
    symbol: '',
    name: 'Major triad',
    family: 'triad',
    priority: 100,
    cause: 'A major third on the bottom and a minor third on top. The wide interval sits low, which is why it reads as bright and settled.',
    categories: ['triad'],
    blurb: 'A major third and a perfect fifth over the root.',
    tones: [t(0, 0), t(2, 4), t(4, 7)],
  },
  {
    id: 'min',
    symbol: 'm',
    name: 'Minor triad',
    family: 'triad',
    priority: 100,
    cause: 'The same fifth as major, but the third is lowered a semitone. Narrow third on the bottom, wide on top — the reverse of major.',
    categories: ['triad'],
    blurb: 'Lower the 3 by a half step and major becomes minor.',
    tones: [t(0, 0), t(2, 3), t(4, 7)],
  },
  {
    id: 'dim',
    symbol: '°',
    name: 'Diminished triad',
    family: 'triad',
    priority: 90,
    cause: 'Two minor thirds stacked. The outer interval is a tritone, so nothing about it feels like a resting place.',
    categories: ['triad'],
    blurb: 'Both the 3 and the 5 are lowered a half step.',
    tones: [t(0, 0), t(2, 3), t(4, 6)],
  },
  {
    id: 'aug',
    symbol: '+',
    name: 'Augmented triad',
    family: 'triad',
    priority: 85,
    cause: 'Two major thirds stacked. It divides the octave evenly, so every inversion sounds like a root position — the ear has no anchor.',
    categories: ['triad'],
    blurb: 'The 5 is raised a half step, so the chord keeps climbing.',
    tones: [t(0, 0), t(2, 4), t(4, 8)],
  },
  {
    id: 'sus2',
    symbol: 'sus2',
    name: 'Suspended 2nd',
    family: 'triad',
    priority: 70,
    cause: 'The third is replaced by a second. With no third there is no major or minor, so the chord is open and unresolved.',
    categories: ['triad', 'suspended'],
    blurb: 'The 3 is swapped for a 2. There is no third at all.',
    tones: [t(0, 0), t(1, 2), t(4, 7)],
  },
  {
    id: 'sus4',
    symbol: 'sus4',
    name: 'Suspended 4th',
    family: 'triad',
    priority: 70,
    cause: 'The third is replaced by a fourth sitting a semitone above it, which pulls downward and wants to resolve into the third.',
    categories: ['triad', 'suspended'],
    blurb: 'The 3 is swapped for a 4. There is no third at all.',
    tones: [t(0, 0), t(3, 5), t(4, 7)],
  },
  {
    id: 'maj7',
    symbol: 'maj7',
    name: 'Major seventh',
    family: 'seventh',
    priority: 100,
    cause: 'A major triad with the leading tone stacked on top. The semitone between the 7th and the root is what makes it shimmer instead of resolve.',
    categories: ['seventh'],
    blurb: 'The 7 sits one half step under the root.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 11)],
  },
  {
    id: 'dom7',
    symbol: '7',
    name: 'Dominant seventh',
    family: 'seventh',
    priority: 100,
    cause: 'Major triad plus a minor seventh. The 3rd and 7th form a tritone, and that tritone is the engine of almost every V-I cadence.',
    categories: ['seventh'],
    blurb: 'The 7 is flattened, so it sits a whole step under the root.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10)],
  },
  {
    id: 'min7',
    symbol: 'm7',
    name: 'Minor seventh',
    family: 'seventh',
    priority: 100,
    cause: 'Minor triad plus a minor seventh. No tritone anywhere inside it, which is why it sits still and works as a resting ii or vi.',
    categories: ['seventh'],
    blurb: 'A minor triad with a flat 7 on top.',
    tones: [t(0, 0), t(2, 3), t(4, 7), t(6, 10)],
  },
  {
    id: 'm7b5',
    symbol: 'ø7',
    name: 'Half-diminished seventh',
    family: 'seventh',
    priority: 90,
    cause: 'Diminished triad with a minor seventh on top. Half the chord is unstable and half is not — hence "half" diminished.',
    categories: ['seventh'],
    blurb: 'A diminished triad with a flat 7, not a double flat 7.',
    aliases: ['m7b5', 'min7b5', 'halfdim7'],
    tones: [t(0, 0), t(2, 3), t(4, 6), t(6, 10)],
  },
  {
    id: 'dim7',
    symbol: '°7',
    name: 'Fully diminished seventh',
    family: 'seventh',
    priority: 80,
    cause: 'Three minor thirds stacked. It splits the octave into four equal parts, so all four inversions sound identical and it can resolve almost anywhere.',
    categories: ['seventh'],
    blurb: 'Every note is a minor third above the last. The 7 is a double flat.',
    aliases: ['dim7'],
    tones: [t(0, 0), t(2, 3), t(4, 6), t(6, 9)],
  },
  {
    id: 'minMaj7',
    symbol: 'mMaj7',
    name: 'Minor-major seventh',
    family: 'seventh',
    priority: 60,
    cause: 'Minor triad with a major seventh. The clash between the minor third and the leading tone is why it sounds like film noir.',
    categories: ['seventh'],
    blurb: 'A minor triad, but the 7 is the natural one.',
    tones: [t(0, 0), t(2, 3), t(4, 7), t(6, 11)],
  },
  {
    id: 'aug7',
    symbol: '7♯5',
    name: 'Augmented seventh',
    family: 'altered',
    priority: 55,
    cause: 'A dominant seventh with the fifth raised. The raised fifth pushes upward at the same time the seventh pulls down.',
    categories: ['seventh', 'altered'],
    blurb: 'A dominant 7 with the 5 raised a half step.',
    aliases: ['7aug5'],
    tones: [t(0, 0), t(2, 4), t(4, 8), t(6, 10)],
  },
  {
    id: 'maj7s5',
    symbol: 'maj7♯5',
    name: 'Major seventh sharp five',
    family: 'altered',
    priority: 50,
    cause: 'An augmented triad with a major seventh. Two major thirds plus a leading tone — floating and unresolved by design.',
    categories: ['seventh', 'altered'],
    blurb: 'A major 7 with the 5 raised a half step.',
    tones: [t(0, 0), t(2, 4), t(4, 8), t(6, 11)],
  },
  {
    id: '6',
    symbol: '6',
    name: 'Major sixth',
    family: 'sixth',
    priority: 75,
    cause: 'A major triad with a sixth added instead of a seventh. It shares all four notes with a minor seventh chord a third below.',
    categories: ['sixth-added'],
    blurb: 'A major triad with a 6 added. There is no 7.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(5, 9)],
  },
  {
    id: 'm6',
    symbol: 'm6',
    name: 'Minor sixth',
    family: 'sixth',
    priority: 70,
    cause: 'Minor triad with a major sixth on top. That major sixth over a minor third is the sound of the melodic minor scale.',
    categories: ['sixth-added'],
    blurb: 'A minor triad with a natural 6 added.',
    tones: [t(0, 0), t(2, 3), t(4, 7), t(5, 9)],
  },
  {
    id: 'maj9',
    symbol: 'maj9',
    name: 'Major ninth',
    family: 'extended',
    priority: 65,
    cause: 'A major seventh chord that keeps stacking thirds. The 9th is a whole step above the root, adding colour without changing the function.',
    categories: ['extended'],
    blurb: 'A major 7 chord that keeps stacking, up to the 9.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 11), t(8, 14)],
  },
  {
    id: 'dom9',
    symbol: '9',
    name: 'Dominant ninth',
    family: 'extended',
    priority: 65,
    cause: 'Dominant seventh plus a ninth. The tritone still drives the resolution; the ninth just makes the ride more colourful.',
    categories: ['extended'],
    blurb: 'A dominant 7 with a 9 on top. The flat 7 is still there.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(8, 14)],
  },
  {
    id: 'min9',
    symbol: 'm9',
    name: 'Minor ninth',
    family: 'extended',
    priority: 65,
    cause: 'Minor seventh plus a ninth. Still no tritone, so it stays restful even though it has five notes.',
    categories: ['extended'],
    blurb: 'A minor 7 with a 9 on top.',
    tones: [t(0, 0), t(2, 3), t(4, 7), t(6, 10), t(8, 14)],
  },
  {
    id: 'dom7b9',
    symbol: '7♭9',
    name: 'Dominant seventh flat nine',
    family: 'altered',
    priority: 45,
    cause: 'A dominant with the ninth lowered a semitone. The top four notes form a diminished seventh, which is where the extra tension comes from.',
    categories: ['extended', 'altered'],
    blurb: 'A dominant 9 with the 9 lowered a half step.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(8, 13)],
  },
  {
    id: 'dom7s9',
    symbol: '7♯9',
    name: 'Dominant seventh sharp nine',
    family: 'altered',
    priority: 45,
    cause: 'The raised ninth is a minor third spelled up an octave, so the chord holds a major and a minor third at once. The Hendrix chord.',
    categories: ['extended', 'altered'],
    blurb: 'A dominant 9 with the 9 raised a half step.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(8, 15)],
  },
  {
    id: 'dom7s11',
    symbol: '7♯11',
    name: 'Dominant seventh sharp eleven',
    family: 'altered',
    priority: 45,
    cause: 'The raised eleventh is a tritone above the root, so the chord contains two tritones pulling in different directions.',
    categories: ['extended', 'altered'],
    blurb: 'A dominant chord with the 11 raised a half step.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(10, 18)],
  },
  {
    id: 'add9',
    symbol: 'add9',
    name: 'Added ninth',
    family: 'sixth',
    categories: ['sixth-added'],
    blurb: 'A major triad with a 9 added. There is no 7.',
    priority: 72,
    cause: 'A ninth stacked on a plain triad. Because no seventh sits between them, the 9 rings as colour rather than as tension — which is exactly what separates it from a dominant ninth.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(8, 14)],
  },
  {
    id: 'madd9',
    symbol: 'm(add9)',
    name: 'Minor added ninth',
    family: 'sixth',
    categories: ['sixth-added'],
    blurb: 'A minor triad with a 9 added. There is no 7.',
    priority: 66,
    cause: 'The same idea as add9 over a minor third. The 9 sits a whole step above the root and a minor third below it — close enough to shimmer without the pull a seventh would add.',
    tones: [t(0, 0), t(2, 3), t(4, 7), t(8, 14)],
  },
  {
    id: 'dom7b5',
    symbol: '7♭5',
    name: 'Dominant seventh flat five',
    family: 'altered',
    categories: ['seventh', 'altered'],
    blurb: 'A dominant 7 with the 5 lowered a half step.',
    priority: 52,
    cause: 'Lowering the fifth puts a second tritone in the chord, alongside the one between the 3rd and the 7th. Two tritones pulling at once is why it sounds like it could go almost anywhere.',
    tones: [t(0, 0), t(2, 4), t(4, 6), t(6, 10)],
  },
  {
    id: 'dom11',
    symbol: '11',
    name: 'Dominant eleventh',
    family: 'extended',
    categories: ['extended'],
    blurb: 'A dominant 9 with an 11 stacked on top.',
    commonlyOmitted: ['3'],
    priority: 42,
    cause: 'Thirds stacked as far as the eleventh. The 11 sits a half step above the 3, so players usually drop the third — but the formula still has it, and the chord is not an eleventh without it.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(8, 14), t(10, 17)],
  },
  {
    id: 'dom13',
    symbol: '13',
    name: 'Dominant thirteenth',
    family: 'extended',
    categories: ['extended'],
    blurb: 'Thirds stacked all the way to the 13.',
    commonlyOmitted: ['5', '11'],
    priority: 40,
    cause: 'Every odd degree from the root to the thirteenth. Seven notes will not fit under two hands, so a played voicing leaves some out — most often the 5 and the 11 — while the formula keeps them.',
    tones: [t(0, 0), t(2, 4), t(4, 7), t(6, 10), t(8, 14), t(10, 17), t(12, 21)],
  },
];

export const QUALITY_BY_ID: Record<string, ChordQuality> = Object.fromEntries(
  QUALITIES.map((q) => [q.id, q]),
);

/** Degree label for a chord tone: "1", "♭3", "♯11", ... */
export function degreeLabel(tone: ChordTone): string {
  const reference = MAJOR_SCALE_SEMITONES[tone.diatonic] ?? tone.diatonic * 2;
  const diff = tone.semitones - reference;
  const prefix = diff === 0 ? '' : diff === -1 ? '♭' : diff === 1 ? '♯' : diff === -2 ? '𝄫' : diff === 2 ? '𝄪' : `${diff}`;
  return `${prefix}${tone.diatonic + 1}`;
}

/** Plain-ASCII degree label, handy for ids and tests. */
export function degreeKey(tone: ChordTone): string {
  return degreeLabel(tone).replace('♭', 'b').replace('♯', '#');
}

/** Build the actual written pitches of a chord from its root. */
export function spellChord(root: Pitch, quality: ChordQuality): Pitch[] {
  // A chord tone *is* an interval above the root — a diatonic distance and a
  // number of semitones — so it is spelled by the same rule as any other
  // transposition. That shared rule is what stops a ♯9 from arriving as a ♭3.
  return quality.tones.map((tone) => transposeBy(root, tone.diatonic, tone.semitones));
}

/**
 * Chord FIB's core move: you know the quality and one degree, so the root is determined.
 * "maj7, and B is the 7th" -> root C, chord Cmaj7.
 */
export function solveRootFromDegree(
  // Kept in the signature so call sites read like the musical statement they model
  // ("this quality, this degree, this note"), even though the interval alone determines the root.
  _quality: ChordQuality,
  tone: ChordTone,
  known: Pitch,
): { root: Pitch; theoretical: boolean } {
  const rootLetterIndex = ((letterIndex(known.letter) - tone.diatonic) % 7 + 7) % 7;
  const rootLetter: Letter = LETTERS[rootLetterIndex];

  // Choose the octave so the known note actually lands `tone.diatonic` steps above the root.
  const rootStep = diatonicStep(known) - tone.diatonic;
  const rootOctave = Math.floor(rootStep / 7);

  const targetClass = ((pitchClass(known) - tone.semitones) % 12 + 12) % 12;
  const natural = naturalPitchClass(rootLetter);
  let alter = targetClass - natural;
  if (alter > 6) alter -= 12;
  if (alter < -6) alter += 12;

  const root: Pitch = { letter: rootLetter, alter, octave: rootOctave };
  return { root, theoretical: Math.abs(alter) > 2 };
}

export interface ChordAnalysis {
  root: Pitch;
  quality: ChordQuality;
  /** Full chord name, including slash bass when the lowest note isn't the root. */
  name: string;
  /** Name without the slash bass. */
  baseName: string;
  /** 0 = root position, 1 = first inversion, ... */
  inversion: number;
  inversionLabel: string;
  bass: Pitch;
  /** 0-1. Lower when notes are missing, doubled oddly, or the spelling fights the analysis. */
  confidence: number;
  /** Human-readable notes on what made this reading imperfect. */
  caveats: string[];
  /** Degree label for each written pitch, in written order. */
  degrees: string[];
  pitches: Pitch[];
}

const INVERSION_LABELS = ['root position', '1st inversion', '2nd inversion', '3rd inversion', '4th inversion'];

function buildName(root: Pitch, quality: ChordQuality, bass: Pitch): { name: string; baseName: string } {
  const baseName = `${pitchName(root)}${quality.symbol}`;
  const rootIsBass = pitchClass(root) === pitchClass(bass);
  return { name: rootIsBass ? baseName : `${baseName}/${pitchName(bass)}`, baseName };
}

/**
 * Identify chords from written pitches. Returns candidates best-first; an empty
 * array means we genuinely could not name it, which the UI says out loud rather
 * than inventing something.
 */
export function analyzeChord(input: Pitch[]): ChordAnalysis[] {
  const pitches = [...input].sort((a, b) => midi(a) - midi(b));
  if (pitches.length < 3) return [];

  const bass = pitches[0];
  const classes = new Set(pitches.map(pitchClass));
  const results: ChordAnalysis[] = [];

  for (const candidateRoot of pitches) {
    for (const quality of QUALITIES) {
      const rootClass = pitchClass(candidateRoot);
      const required = quality.tones.map((tone) => (rootClass + tone.semitones) % 12);
      const requiredSet = new Set(required);

      // Every written pitch class must belong to the chord.
      let extraneous = false;
      for (const cls of classes) if (!requiredSet.has(cls)) extraneous = true;
      if (extraneous) continue;

      const missing = required.filter((cls) => !classes.has(cls));
      // Allow at most one omission, and never the third or the seventh — those carry the identity.
      if (missing.length > 1) continue;
      const caveats: string[] = [];
      if (missing.length === 1) {
        const missingTone = quality.tones.find((tone) => (rootClass + tone.semitones) % 12 === missing[0])!;
        const label = degreeLabel(missingTone);
        if (label !== '5') continue; // only a fifth may be omitted
        caveats.push(`The 5th is not written — the chord still reads as ${quality.name.toLowerCase()} without it.`);
      }

      const spelled = spellChord(candidateRoot, quality);
      const spellingMatches = pitches.every((p) =>
        spelled.some((s) => s.letter === p.letter && s.alter === p.alter),
      );
      if (!spellingMatches) {
        caveats.push('Written enharmonically — the letters do not stack in thirds for this reading.');
      }

      const bassTone = quality.tones.find((tone) => (rootClass + tone.semitones) % 12 === pitchClass(bass));
      const inversion = bassTone ? quality.tones.indexOf(bassTone) : 0;

      const { name, baseName } = buildName(candidateRoot, quality, bass);

      let confidence = quality.priority / 100;
      if (missing.length) confidence -= 0.15;
      if (!spellingMatches) confidence -= 0.3;
      if (inversion > 0) confidence -= 0.05 * inversion;
      confidence = Math.max(0.05, Math.min(0.99, confidence));

      results.push({
        root: candidateRoot,
        quality,
        name,
        baseName,
        inversion,
        inversionLabel: INVERSION_LABELS[inversion] ?? `${inversion}th inversion`,
        bass,
        confidence,
        caveats,
        degrees: pitches.map((p) => {
          const tone = quality.tones.find((tn) => (rootClass + tn.semitones) % 12 === pitchClass(p));
          return tone ? degreeLabel(tone) : '?';
        }),
        pitches,
      });
    }
  }

  // De-duplicate identical names, keep the strongest reading of each.
  const seen = new Map<string, ChordAnalysis>();
  for (const r of results) {
    const existing = seen.get(r.name);
    if (!existing || r.confidence > existing.confidence) seen.set(r.name, r);
  }

  return [...seen.values()].sort((a, b) => b.confidence - a.confidence).slice(0, 4);
}

/** Random chord for practice prompts. */
export function randomChord(qualityPool: ChordQuality[], octave = 4): { root: Pitch; quality: ChordQuality } {
  const quality = qualityPool[Math.floor(Math.random() * qualityPool.length)];
  const roots: Pitch[] = [
    { letter: 'C', alter: 0, octave },
    { letter: 'D', alter: 0, octave },
    { letter: 'E', alter: 0, octave },
    { letter: 'F', alter: 0, octave },
    { letter: 'G', alter: 0, octave },
    { letter: 'A', alter: 0, octave },
    { letter: 'B', alter: -1, octave },
    { letter: 'E', alter: -1, octave },
    { letter: 'A', alter: -1, octave },
    { letter: 'F', alter: 1, octave },
  ];
  return { root: roots[Math.floor(Math.random() * roots.length)], quality };
}

// ------------------------------------------------------------ the formula

/**
 * The chord's formula, as a learner reads it: `1–3–5–♭7`.
 *
 * Derived from the quality's own degree structure, never written down. A second
 * hand-maintained table of formulas is the defect this exists to prevent: it
 * would agree with `spellChord` on the day it was written and drift the first
 * time a quality changed, and the drift would be a screen teaching a chord the
 * rest of the app does not build.
 *
 * En dashes, not hyphens — `1–3–5` is a range of degrees, and it is what the
 * printed convention uses.
 */
export function chordFormula(quality: ChordQuality): string {
  return quality.tones.map(degreeLabel).join('–');
}

/** The same, as separate degrees, for a screen that wants to lay them out. */
export function chordFormulaDegrees(quality: ChordQuality): string[] {
  return quality.tones.map(degreeLabel);
}

/**
 * The degrees a playable voicing keeps, when it cannot keep them all.
 *
 * Returned separately from the formula and never in place of it. A thirteenth
 * has seven notes and two hands have ten fingers; what a guitarist plays is a
 * *voicing* of the chord, not the chord. Showing one as the other teaches a
 * formula with three notes missing.
 */
export function commonVoicingDegrees(quality: ChordQuality): string[] {
  const omitted = new Set(quality.commonlyOmitted ?? []);
  return chordFormulaDegrees(quality).filter((degree) => !omitted.has(degree));
}
